import React, { useEffect, useRef, useState } from 'react';
import { api as httpApi } from '../../api/client';
import './SitePet.css';
import { createModeController } from './modes';
import type { Behavior, Mood, PetController, PetVariant } from './modes';

export const PET_STORAGE_KEY = 'avm.site-pet';
export type { PetVariant };

const POSITION_KEY = 'avm.site-pet-pos';
const AI_MESSAGE_LAST_KEY = 'avm.site-pet-ai-last';
const BASE_PET_SIZE = 40;
const EDGE_MARGIN = 20;
const WALK_SPEED = 1.2;
const PROXIMITY_DIST = 150;
const PLAY_DIST = 60;
const BUBBLE_COOLDOWN_MS = 30000;
const BUBBLE_DURATION_MS = 4000;
const IDLE_TO_REST_MS = 20000;
const IDLE_TO_SLEEP_MS = 45000;
const WALK_MIN_MS = 3000;
const WALK_MAX_MS = 8000;
const IDLE_MIN_MS = 3000;
const IDLE_MAX_MS = 10000;
const DOM_SCAN_MS = 5000;
const AI_MESSAGE_COOLDOWN_MS = 10 * 60 * 1000;
const AI_MESSAGE_MIN_SESSION_MS = 15000;

const VALID_MOODS: ReadonlySet<string> = new Set<Mood>([
  'idle', 'walking', 'resting', 'sleeping', 'thinking',
  'alert', 'playful', 'startled', 'happy', 'sad',
]);

interface AvmPetAPI {
  say(text: string): void;
  react(mood: Mood): void;
  hide(): void;
  show(): void;
  setEnabled(enabled: boolean): void;
  getState(): {
    enabled: boolean;
    visible: boolean;
    mood: Mood;
    variant: PetVariant;
    position: { x: number; y: number };
    bubble: string | null;
  };
}

let historyPatchRefs = 0;
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;

function attachHistoryRouteSignal(): () => void {
  if (historyPatchRefs === 0) {
    originalPushState = window.history.pushState;
    originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushStateWithPetSignal(
      ...args: Parameters<History['pushState']>
    ) {
      const result = originalPushState!.apply(this, args);
      window.dispatchEvent(new Event('avm-pet-route-change'));
      return result;
    };

    window.history.replaceState = function replaceStateWithPetSignal(
      ...args: Parameters<History['replaceState']>
    ) {
      const result = originalReplaceState!.apply(this, args);
      window.dispatchEvent(new Event('avm-pet-route-change'));
      return result;
    };
  }

  historyPatchRefs += 1;

  return () => {
    historyPatchRefs = Math.max(0, historyPatchRefs - 1);
    if (historyPatchRefs === 0 && originalPushState && originalReplaceState) {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      originalPushState = null;
      originalReplaceState = null;
    }
  };
}

declare global {
  interface Window {
    avmPet?: AvmPetAPI;
  }
}

const BUBBLE_POOLS: Record<string, string[]> = {
  route: ['Новый маршрут...', 'Куда это мы?', 'Интересно...'],
  error: ['Ой!', 'Что-то сломалось!', 'Ошибка...'],
  wake: ['Я спал?', 'Доброе утро!', 'Сколько времени?'],
  greet: ['Привет!', 'Ты здесь!', '👋'],
  random: ['*думает*', '*наблюдает*', 'Хмм...'],
};

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function isEnabled(value: string | null): boolean {
  return value !== 'none' && value !== null;
}

function resolveVariant(value: string | null): PetVariant {
  switch (value) {
    case 'ghost':
    case 'clawd':
      return value;
    // Migrate old variants to clawd
    case 'oneko':
    case 'roaming':
    case 'spider':
      localStorage.setItem(PET_STORAGE_KEY, 'clawd');
      return 'clawd';
    default:
      return 'none';
  }
}

function loadPosition(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { x: number; y: number };
      if (typeof p.x === 'number' && typeof p.y === 'number') return p;
    }
  } catch { /* ignore */ }
  return null;
}

function createPetController(el: HTMLDivElement, variant: PetVariant): { destroy(): void } {
  let destroyed = false;
  let visible = true;
  let mood: Mood = 'idle';
  let behavior: Behavior = 'idle';
  let behaviorTimer = 0;
  let behaviorDeadline = 0;
  let userIdleSince = Date.now();
  const mountedAt = Date.now();
  let lastBubbleTime = 0;
  let aiRequestInFlight = false;
  let recentSignals: string[] = [];
  let bubbleTimeout: ReturnType<typeof setTimeout> | null = null;
  let rafId: number | null = null;
  let lastTs = 0;
  let moodOverride: { mood: Mood; until: number } | null = null;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const petSize = el.offsetWidth || BASE_PET_SIZE;
  const savedPos = loadPosition();
  const pos = savedPos ?? {
    x: EDGE_MARGIN + Math.random() * Math.max(1, window.innerWidth - petSize - EDGE_MARGIN * 2),
    y: EDGE_MARGIN + Math.random() * Math.max(1, window.innerHeight - petSize - EDGE_MARGIN * 2),
  };
  pos.x = clamp(pos.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerWidth - petSize - EDGE_MARGIN));
  pos.y = clamp(pos.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, window.innerHeight - petSize - EDGE_MARGIN));
  let targetX = pos.x;
  let targetY = pos.y;

  // --- Mode controller: owns the pet's visual rendering for this variant. ---
  const controller: PetController = createModeController(variant, {
    el,
    reducedMotion,
    initialPos: { x: pos.x, y: pos.y },
  });
  const drivesPosition = controller.drivesPosition;

  const bubble = document.createElement('div');
  bubble.className = 'site-pet-bubble';
  el.appendChild(bubble);

  if (drivesPosition) {
    el.style.left = `${pos.x}px`;
    el.style.top = `${pos.y}px`;
  }

  function setMood(m: Mood) {
    mood = m;
    controller.setMood(m);
  }

  function applyMood() {
    if (moodOverride && Date.now() < moodOverride.until) return;
    moodOverride = null;
    switch (behavior) {
      case 'walking': setMood('walking'); break;
      case 'idle': setMood('idle'); break;
      case 'resting': setMood('resting'); break;
      case 'sleeping': setMood('sleeping'); break;
    }
  }

  function setBehavior(b: Behavior) {
    behavior = b;
    behaviorTimer = 0;
    behaviorDeadline = Date.now() + (
      b === 'walking'
        ? WALK_MIN_MS + Math.random() * (WALK_MAX_MS - WALK_MIN_MS)
        : b === 'idle'
          ? IDLE_MIN_MS + Math.random() * (IDLE_MAX_MS - IDLE_MIN_MS)
          : 10000 + Math.random() * 15000
    );
    controller.setBehavior?.(b);
    if (b === 'walking') pickTarget();
    applyMood();
  }

  function pickTarget() {
    const sz = el.offsetWidth || petSize;
    targetX = EDGE_MARGIN + Math.random() * Math.max(1, window.innerWidth - sz - EDGE_MARGIN * 2);
    targetY = EDGE_MARGIN + Math.random() * Math.max(1, window.innerHeight - sz - EDGE_MARGIN * 2);
  }

  function showBubble(text: string, force = false) {
    if (!visible || destroyed) return;
    const now = Date.now();
    if (!force && now - lastBubbleTime < BUBBLE_COOLDOWN_MS) return;
    lastBubbleTime = now;
    bubble.textContent = text;
    bubble.style.display = 'block';
    if (bubbleTimeout) clearTimeout(bubbleTimeout);
    bubbleTimeout = setTimeout(() => {
      bubble.style.display = 'none';
      bubbleTimeout = null;
    }, BUBBLE_DURATION_MS);
  }

  function tryBubble(pool: string[]): boolean {
    const now = Date.now();
    if (now - lastBubbleTime < BUBBLE_COOLDOWN_MS) return false;
    showBubble(pick(pool));
    return true;
  }

  function noteSignal(signal: string) {
    recentSignals = [signal, ...recentSignals.filter((item) => item !== signal)].slice(0, 5);
  }

  function getLastAiMessageAt(): number {
    const raw = localStorage.getItem(AI_MESSAGE_LAST_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function setLastAiMessageAt(value: number) {
    try {
      localStorage.setItem(AI_MESSAGE_LAST_KEY, String(value));
    } catch { /* ignore */ }
  }

  async function requestAiBubble(trigger: string) {
    if (destroyed || !visible || aiRequestInFlight) return;

    const now = Date.now();
    if (now - mountedAt < AI_MESSAGE_MIN_SESSION_MS) return;
    if (now - getLastAiMessageAt() < AI_MESSAGE_COOLDOWN_MS) return;

    aiRequestInFlight = true;
    setLastAiMessageAt(now);
    try {
      const response = await httpApi.post(
        '/api/v1/ai/pet-message',
        {
          trigger,
          route: `${window.location.pathname}${window.location.search}`.slice(0, 120),
          mood,
          recent_signals: recentSignals,
        },
        { timeout: 12000 }
      );
      const text = String(response.data?.text || '').trim();
      if (text) {
        showBubble(text, true);
      }
    } catch {
      // AI pet messages are optional; local behavior must stay silent and cheap on failure.
    } finally {
      aiRequestInFlight = false;
    }
  }

  function tempMood(m: Mood, durationMs: number) {
    moodOverride = { mood: m, until: Date.now() + durationMs };
    setMood(m);
  }

  function savePosition() {
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify({ x: Math.round(pos.x), y: Math.round(pos.y) }));
    } catch { /* ignore */ }
  }

  // --- Main loop ---
  function tick(ts: number) {
    if (destroyed) return;
    rafId = requestAnimationFrame(tick);
    const dt = lastTs ? (ts - lastTs) / 1000 : 0.016;
    const dtMs = lastTs ? ts - lastTs : 16;
    lastTs = ts;

    // Controllers that animate (e.g. ghost cursor trail) advance every frame,
    // regardless of behavior state.
    controller.tick?.(dtMs);

    if (behavior === 'sleeping') return;

    behaviorTimer += dt * 1000;
    const idleTime = Date.now() - userIdleSince;

    // Position FSM only runs for controllers that delegate movement to the core.
    if (drivesPosition && behavior === 'walking') {
      const dx = targetX - pos.x;
      const dy = targetY - pos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 5 || Date.now() > behaviorDeadline) {
        setBehavior('idle');
      } else {
        const speed = reducedMotion ? 0.3 : WALK_SPEED;
        const step = Math.min(speed * dt * 60, dist);
        pos.x += (dx / dist) * step;
        pos.y += (dy / dist) * step;
        pos.x = clamp(pos.x, EDGE_MARGIN, window.innerWidth - petSize - EDGE_MARGIN);
        pos.y = clamp(pos.y, EDGE_MARGIN, window.innerHeight - petSize - EDGE_MARGIN);
        el.style.left = `${pos.x}px`;
        el.style.top = `${pos.y}px`;
      }
    }

    if (behavior === 'idle') {
      if (idleTime > IDLE_TO_SLEEP_MS) {
        setBehavior('sleeping');
      } else if (idleTime > IDLE_TO_REST_MS && behaviorTimer > 5000) {
        setBehavior('resting');
      } else if (Date.now() > behaviorDeadline) {
        setBehavior('walking');
      } else if (behaviorTimer > 5000 && Math.random() < 0.002) {
        tempMood('thinking', 3000);
        tryBubble(BUBBLE_POOLS.random);
      }
    }

    if (behavior === 'resting') {
      if (idleTime > IDLE_TO_SLEEP_MS) {
        setBehavior('sleeping');
      } else if (idleTime < IDLE_TO_REST_MS - 5000) {
        setBehavior('idle');
      } else if (Date.now() > behaviorDeadline) {
        setBehavior('walking');
      }
    }

    applyMood();
  }

  rafId = requestAnimationFrame(tick);

  // --- Signals ---
  const onMouseMove = (e: MouseEvent) => {
    if (destroyed || !visible || document.hidden) return;
    userIdleSince = Date.now();

    if (behavior === 'sleeping') {
      if (document.visibilityState === 'visible') {
        setBehavior('idle');
        tryBubble(BUBBLE_POOLS.wake);
      }
      return;
    }

    // Proximity moods are relative to the pet's visual center, which each
    // controller reports via getPosition().
    const p = controller.getPosition();
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < PLAY_DIST) {
      noteSignal('cursor_close');
      tempMood('playful', 2000);
    } else if (dist < PROXIMITY_DIST) {
      noteSignal('cursor_near');
      tempMood('alert', 1500);
    }
  };
  document.addEventListener('mousemove', onMouseMove);

  const onVisibility = () => {
    if (destroyed) return;
    if (document.hidden) {
      setBehavior('sleeping');
    } else if (behavior === 'sleeping') {
      setBehavior('idle');
      tryBubble(BUBBLE_POOLS.wake);
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  const onRoute = () => {
    if (destroyed || !visible) return;
    noteSignal('route_change');
    tempMood('thinking', 3000);
    tryBubble(BUBBLE_POOLS.route);
    void requestAiBubble('route_change');
  };
  window.addEventListener('popstate', onRoute);
  const detachHistoryRouteSignal = attachHistoryRouteSignal();
  window.addEventListener('avm-pet-route-change', onRoute);

  const onError = () => {
    if (destroyed || !visible) return;
    noteSignal('frontend_error');
    tempMood('startled', 3000);
    tryBubble(BUBBLE_POOLS.error);
    void requestAiBubble('frontend_error');
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onError);

  const onSiteNotification = () => {
    if (destroyed || !visible) return;
    noteSignal('site_notification');
    tempMood('alert', 2500);
    void requestAiBubble('site_notification');
  };
  window.addEventListener('site-notification-trigger', onSiteNotification);

  const domScanTimer = window.setInterval(() => {
    if (destroyed || !visible || document.hidden) return;
    const hasAlert = Boolean(document.querySelector('[role="alert"], .error, .inbox-error-banner, .pd-error, .mrr-error-block'));
    const hasLoading = Boolean(document.querySelector('[aria-busy="true"], .loading, .spinner, .mrr-progress-hero, .inbox-upload-progress'));

    if (hasAlert) {
      noteSignal('dom_alert');
      tempMood('alert', 2500);
      tryBubble(BUBBLE_POOLS.error);
      void requestAiBubble('dom_alert');
    } else if (hasLoading && behavior !== 'sleeping') {
      noteSignal('loading_state');
      tempMood('thinking', 2000);
    }
  }, DOM_SCAN_MS);

  const onResize = () => {
    if (destroyed) return;
    if (!drivesPosition) return;
    const sz = el.offsetWidth || petSize;
    pos.x = clamp(pos.x, EDGE_MARGIN, window.innerWidth - sz - EDGE_MARGIN);
    pos.y = clamp(pos.y, EDGE_MARGIN, window.innerHeight - sz - EDGE_MARGIN);
    el.style.left = `${pos.x}px`;
    el.style.top = `${pos.y}px`;
  };
  window.addEventListener('resize', onResize);

  // --- Drag (only for controllers that delegate position to the core) ---
  let dragging = false;
  let dragOffX = 0;
  let dragOffY = 0;

  const onPDown = (e: PointerEvent) => {
    dragging = true;
    el.setPointerCapture(e.pointerId);
    dragOffX = e.clientX - pos.x;
    dragOffY = e.clientY - pos.y;
    el.classList.add('site-pet-dragging');
    userIdleSince = Date.now();
  };

  const onPMove = (e: PointerEvent) => {
    if (!dragging) return;
    pos.x = clamp(e.clientX - dragOffX, 0, window.innerWidth - (el.offsetWidth || petSize));
    pos.y = clamp(e.clientY - dragOffY, 0, window.innerHeight - (el.offsetHeight || petSize));
    el.style.left = `${pos.x}px`;
    el.style.top = `${pos.y}px`;
  };

  const onPUp = (_e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    el.classList.remove('site-pet-dragging');
    savePosition();
  };

  if (controller.draggable) {
    el.addEventListener('pointerdown', onPDown);
    el.addEventListener('pointermove', onPMove);
    el.addEventListener('pointerup', onPUp);
    el.addEventListener('pointercancel', onPUp);
  }

  // --- CustomEvent command listener ---
  const onCommand = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (!detail || (typeof detail.command !== 'string' && typeof detail.type !== 'string')) return;
    const command = detail.command ?? detail.type;
    const payload = detail.payload ?? detail.text ?? detail.mood ?? detail.enabled;
    switch (command) {
      case 'say':
        if (typeof payload === 'string') showBubble(payload, true);
        break;
      case 'react':
        if (typeof payload === 'string' && VALID_MOODS.has(payload)) {
          tempMood(payload as Mood, 3000);
        }
        break;
      case 'hide':
        el.style.display = 'none';
        visible = false;
        break;
      case 'show':
        el.style.display = 'block';
        visible = true;
        break;
      case 'setEnabled':
        if (typeof payload === 'boolean') {
          localStorage.setItem(PET_STORAGE_KEY, payload ? 'clawd' : 'none');
          window.dispatchEvent(new CustomEvent('avm.site-pet-changed'));
        }
        break;
    }
  };
  window.addEventListener('avm-pet-command', onCommand);

  // --- Initial greeting ---
  const greetTimer = window.setTimeout(() => {
    if (!destroyed) tryBubble(BUBBLE_POOLS.greet);
  }, 2000);

  // --- Window API ---
  const api: AvmPetAPI = {
    say(text: string) { showBubble(text, true); },
    react(m: Mood) { tempMood(m, 3000); },
    hide() { el.style.display = 'none'; visible = false; },
    show() { el.style.display = 'block'; visible = true; },
    setEnabled(enabled: boolean) {
      localStorage.setItem(PET_STORAGE_KEY, enabled ? 'clawd' : 'none');
      window.dispatchEvent(new CustomEvent('avm.site-pet-changed'));
    },
    getState() {
      const p = controller.getPosition();
      return {
        enabled: isEnabled(localStorage.getItem(PET_STORAGE_KEY)),
        visible,
        mood,
        variant,
        position: { x: Math.round(p.x), y: Math.round(p.y) },
        bubble: bubble.textContent || null,
      };
    },
  };
  window.avmPet = api;

  return {
    destroy() {
      destroyed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('popstate', onRoute);
      window.removeEventListener('avm-pet-route-change', onRoute);
      detachHistoryRouteSignal();
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onError);
      window.removeEventListener('site-notification-trigger', onSiteNotification);
      window.removeEventListener('resize', onResize);
      window.clearInterval(domScanTimer);
      if (controller.draggable) {
        el.removeEventListener('pointerdown', onPDown);
        el.removeEventListener('pointermove', onPMove);
        el.removeEventListener('pointerup', onPUp);
        el.removeEventListener('pointercancel', onPUp);
      }
      window.removeEventListener('avm-pet-command', onCommand);
      if (bubbleTimeout) clearTimeout(bubbleTimeout);
      window.clearTimeout(greetTimer);
      controller.destroy();
      bubble.remove();
      if (window.avmPet === api) delete window.avmPet;
    },
  };
}

// --- React component ---
const SitePet: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [variant, setVariant] = useState<PetVariant>(() => resolveVariant(localStorage.getItem(PET_STORAGE_KEY)));
  const ctrl = useRef<ReturnType<typeof createPetController> | null>(null);

  useEffect(() => {
    if (variant === 'none' || !ref.current) return;
    ctrl.current = createPetController(ref.current, variant);
    return () => {
      ctrl.current?.destroy();
      ctrl.current = null;
    };
  }, [variant]);

  useEffect(() => {
    const sync = () => setVariant(resolveVariant(localStorage.getItem(PET_STORAGE_KEY)));
    window.addEventListener('avm.site-pet-changed', sync);
    return () => window.removeEventListener('avm.site-pet-changed', sync);
  }, []);

  if (variant === 'none') return null;

  return <div ref={ref} className={`site-pet site-pet--${variant}`} aria-hidden="true" />;
};

export default SitePet;
